
import React from 'react';
import { useParticleSystem } from '../hooks/useParticleSystem';

export interface CustomCursorProps {
  /**
   * Maximum number of particles (default: 15)
   */
  maxParticles?: number;
  
  /**
   * Color or colors for particles
   * Can be a single color string or an array of color strings
   */
  colors?: string[];
  
  /**
   * How particles behave when following the cursor
   */
  trailMode?: 'follow' | 'burst' | 'gentle';
  
  /**
   * How quickly particles fade out (0.01-0.1)
   */
  fadeSpeed?: number;
  
  /**
   * How long particles last (multiplier for fadeSpeed)
   */
  lifespan?: number;
  
  /**
   * Size range for particles
   */
  particleSize?: {
    min: number;
    max: number;
  };
  
  /**
   * How much particles move (0.1-2.0)
   */
  velocityRange?: number;
}

const CustomCursor: React.FC<CustomCursorProps> = ({
  maxParticles = 15,
  colors = ['rgba(255, 255, 255, 0.7)'],
  trailMode = 'follow',
  fadeSpeed = 0.02,
  lifespan = 1,
  particleSize = { min: 0.5, max: 2 },
  velocityRange = 0.5
}) => {
  const { canvasRef, position } = useParticleSystem({
    maxParticles,
    colors,
    trailMode,
    fadeSpeed,
    lifespan,
    particleSize,
    velocityRange
  });

  // Don't render on touch devices
  if (typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches) {
    return null;
  }

  return (
    <>
      <canvas
        ref={canvasRef}
        className="fixed top-0 left-0 pointer-events-none z-50 mix-blend-screen"
        width={typeof window !== 'undefined' ? window.innerWidth : 1920}
        height={typeof window !== 'undefined' ? window.innerHeight : 1080}
      />
      <div
        className="fixed w-3 h-3 bg-white rounded-full pointer-events-none z-50"
        style={{
          left: position.x,
          top: position.y,
          transform: 'translate(-50%, -50%)',
          mixBlendMode: 'difference'
        }}
      />
    </>
  );
};

export default CustomCursor;
